import React, { useMemo } from 'react';
import JoditEditor from 'jodit-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  minHeight?: number;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing your content here...',
  height,
  minHeight = 150,
}: RichTextEditorProps) {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const config = useMemo(
    () => ({
      readonly: false,
      height: height,
      minHeight: minHeight,
      placeholder: placeholder,
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'ul',
        'ol',
        '|',
        'font',
        'fontsize',
        'brush',
        'paragraph',
        '|',
        'image',
        'table',
        'link',
        '|',
        'align',
        'undo',
        'redo',
        'hr',
        'eraser',
      ],
      removeButtons: ['source', 'fullsize', 'about', 'print'],
      uploader: {
        url: `${import.meta.env.VITE_API_BASE_URL || '/api'}/upload`,
        format: 'json',
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        buildData: (data: FormData) => {
          if (data.get && data.get('files[0]')) {
            const file = data.get('files[0]');
            if (file) {
              data.delete('files[0]');
              data.append('file', file);
            }
          }
          return data;
        },
        isSuccess: (resp: any) => !!resp.url,
        process: (resp: any) => {
          const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002'; // fallback
          const fileUrl = resp.url.startsWith('http')
            ? resp.url
            : `${baseUrl}${resp.url.startsWith('/') ? '' : '/'}${resp.url}`;
          return {
            files: [fileUrl],
            path: '',
            baseurl: '',
            error: 0,
            msg: '',
          };
        },
        defaultHandlerSuccess: function (this: any, data: any) {
          if (data.files && data.files.length) {
            this.s.insertImage(data.files[0]);
          }
        },
      },
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
    }),
    [token, placeholder, height, minHeight]
  );

  return (
    <div className="rounded-lg overflow-hidden border border-border bg-white text-black jodit-wrapper">
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .jodit-wrapper ul, .jodit-wysiwyg ul { list-style-type: disc !important; list-style-position: inside !important; padding-left: 1.5rem !important; margin-bottom: 1rem !important; }
        .jodit-wrapper ol, .jodit-wysiwyg ol { list-style-type: decimal !important; list-style-position: inside !important; padding-left: 1.5rem !important; margin-bottom: 1rem !important; }
        .jodit-wrapper li, .jodit-wysiwyg li { display: list-item !important; }
      `,
        }}
      />
      <JoditEditor
        value={value || ''}
        config={config}
        onBlur={(newContent) => onChange(newContent)}
      />
    </div>
  );
}
